export interface PlatformCoupon {
  id: string;
  code: string;
  kind: 'discount' | 'referral';
  discountType: 'percent' | 'amount' | 'free_months';
  discountValue: number;
  durationType: 'once' | 'repeating' | 'forever';
  durationMonths: number | null;
  assignedChurchId: string | null;
  assignedChurchName: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  validFrom: string | null;
  validUntil: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

type Translate = (key: string, options?: Record<string, string | number>) => string;

export const couponDurationLabel = (coupon: PlatformCoupon, t: Translate): string => {
  if (coupon.discountType === 'free_months' || coupon.durationType === 'once') {
    return t('billing.coupons.durations.once');
  }
  if (coupon.durationType === 'forever') return t('billing.coupons.durations.forever');
  return t('billing.coupons.durations.months', { count: coupon.durationMonths ?? 0 });
};

export const couponDiscountLabel = (
  coupon: PlatformCoupon,
  t: Translate,
  formatAmount: (minor: number) => string,
): string => {
  if (coupon.discountType === 'percent') {
    return t('billing.coupons.discountValuePercent', { value: coupon.discountValue });
  }
  if (coupon.discountType === 'free_months') {
    return t('billing.coupons.discountValueMonths', { count: coupon.discountValue });
  }
  return formatAmount(coupon.discountValue);
};

export const canEditCoupon = (coupon: Pick<PlatformCoupon, 'redemptionCount'>): boolean =>
  coupon.redemptionCount === 0;

export const durationPresetFromCoupon = (
  coupon: Pick<PlatformCoupon, 'discountType' | 'durationType' | 'durationMonths'>,
): { preset: 'once' | '1' | '3' | '6' | '12' | 'custom' | 'forever'; customMonths: string } => {
  if (coupon.discountType === 'free_months' || coupon.durationType === 'once') {
    return { preset: 'once', customMonths: '' };
  }
  if (coupon.durationType === 'forever') return { preset: 'forever', customMonths: '' };
  const months = coupon.durationMonths;
  if (months === 1 || months === 3 || months === 6 || months === 12) {
    return { preset: String(months) as '1' | '3' | '6' | '12', customMonths: '' };
  }
  return { preset: 'custom', customMonths: months != null ? String(months) : '' };
};

export const couponDateToStartUtc = (ymd: string): string => `${ymd}T00:00:00.000Z`;

export const couponDateToEndUtc = (ymd: string): string => `${ymd}T23:59:59.999Z`;

export const couponIsoToCalendarDate = (value: string | null): string | null =>
  value && value.length >= 10 ? value.slice(0, 10) : null;

export const couponUsesLabel = (coupon: PlatformCoupon, t: Translate): string =>
  t('billing.coupons.usesValue', {
    used: coupon.redemptionCount,
    max: coupon.maxRedemptions ?? t('billing.coupons.unlimited'),
  });
